import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { submitAmbassadorApplication } from "../../common/services/ambassadorship";
import "./Ambassadors.css";

const LEVELS = [
  "100 Level",
  "200 Level",
  "300 Level",
  "400 Level",
  "500 Level",
  "600 Level",
  "Postgraduate",
];

interface AmbassadorFormData {
  fullName: string;
  email: string;
  phoneNumber: string;
  linkedinUrl: string;
  department: string;
  universityName: string;
  level: string;
  faculty: string;
}

const initialFormData: AmbassadorFormData = {
  fullName: "",
  email: "",
  phoneNumber: "",
  linkedinUrl: "",
  department: "",
  universityName: "",
  level: "",
  faculty: "",
};

const ApplicationForm = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AmbassadorFormData>(initialFormData);
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await submitAmbassadorApplication(formData);

      setFormData(initialFormData);
      await Swal.fire({
        title: "Application received!",
        text: "Thanks for applying — we'll reach out with next steps soon.",
        icon: "success",
        confirmButtonText: "OK",
      });
      navigate("/ambassadors");
    } catch (error: any) {
      console.error("Error submitting ambassador application: ", error);
      Swal.fire({
        title: "Something went wrong",
        text:
          error?.message || "We couldn't submit your application. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="amb-page amb-form-page">
      <div className="amb-inner">
        <Link to="/ambassadors/rewards" className="amb-back">
          ← Back
        </Link>

        <span className="amb-wordmark">
          squaay<span className="dot">.</span>
        </span>

        <div className="amb-headline">
          Join the <em>squad.</em>
        </div>
        <div className="amb-sub">
          Fill in your details and we'll be in touch to get you started.
        </div>

        <form className="amb-form" onSubmit={handleSubmit}>
          <div className="amb-field">
            <label htmlFor="fullName">Full name</label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Your full name"
              required
            />
          </div>

          <div className="amb-field">
            <label htmlFor="email">Email address</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="amb-field">
            <label htmlFor="phoneNumber">Phone number</label>
            <input
              type="tel"
              id="phoneNumber"
              name="phoneNumber"
              value={formData.phoneNumber}
              onChange={handleChange}
              placeholder="+234 (XXX) XXX-XXXX"
              required
            />
          </div>

          <div className="amb-field">
            <label htmlFor="linkedinUrl">LinkedIn profile (optional)</label>
            <input
              type="url"
              id="linkedinUrl"
              name="linkedinUrl"
              value={formData.linkedinUrl}
              onChange={handleChange}
              placeholder="https://linkedin.com/in/your-name"
            />
          </div>

          <div className="amb-field">
            <label htmlFor="universityName">University name</label>
            <input
              type="text"
              id="universityName"
              name="universityName"
              value={formData.universityName}
              onChange={handleChange}
              placeholder="e.g. University of Lagos"
              required
            />
          </div>

          <div className="amb-field">
            <label htmlFor="faculty">Faculty</label>
            <input
              type="text"
              id="faculty"
              name="faculty"
              value={formData.faculty}
              onChange={handleChange}
              placeholder="e.g. Engineering"
              required
            />
          </div>

          <div className="amb-field">
            <label htmlFor="department">Department</label>
            <input
              type="text"
              id="department"
              name="department"
              value={formData.department}
              onChange={handleChange}
              placeholder="e.g. Computer Science"
              required
            />
          </div>

          <div className="amb-field">
            <label htmlFor="level">Level</label>
            <select
              id="level"
              name="level"
              value={formData.level}
              onChange={handleChange}
              required
            >
              <option value="" disabled>
                Select your level
              </option>
              {LEVELS.map((level) => (
                <option value={level} key={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className="amb-pill amb-pill--block amb-form-submit"
            disabled={loading}
          >
            {loading ? "Submitting..." : "Submit application"}
          </button>

          <p className="amb-form-note">
            By applying, you agree to be contacted by the Squaay team about
            the Campus Ambassador program.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ApplicationForm;
